import { lights } from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { PlayerController } from '../../engine/character/PlayerController';
import { PLAYER_CENTER_HEIGHT } from '../../engine/character/PlayerController';
import { GravityField } from '../../engine/gravity/GravityField';
import { HollowWallCollider } from '../../engine/physics/HollowWallCollider';
import type { PhysicsWorld } from '../../engine/physics/PhysicsWorld';
import {
  createSunMaterial,
  createTunnelMaterial,
  createWallMaterial,
  type HollowGpu,
  makeHollowGpu,
} from '../../engine/render/materials/sandboxMaterials';
import type { RenderOrigin } from '../../engine/render/RenderOrigin';
import { type HollowDef, makeHollow } from '../../engine/universe/HollowDef';
import { buildDropShaft, buildSpiralTunnel, buildTube, type TunnelDef } from '../../engine/universe/Tunnel';
import type { GameWorld } from './GameWorld';

/**
 * M1 sandbox: two smooth hollows (2 km and 1 km radius) ~10,000 km from the
 * universe origin, joined by a drop shaft and a walkable spiral tunnel.
 */
export class SandboxScene implements GameWorld {
  readonly name = 'Sandbox';
  readonly ambience = { wind: { gain: 0.6, cutoff: 450 } };
  readonly hints = [
    'Combat test: hold the left mouse button to wind up, release to strike. 1–5 change weapon. Weapons hit only what they pass through: aim; a near miss glances for half damage.',
    'Every weapon has three bonuses: rotation (swing through after the release: they fly the way you swing), finesse (swing out and back: a feint; the blue dot marks where it counts from), movement (run in, jump in, meet a charge).',
    'Each enemy wants one: feint round the knight, spin the slime and brute, charge or jump in on the raider, meet the charger. K puts them back, J shows the combat log.',
    'F throws a stone; the tunnel mouths turn gravity round.',
  ];
  readonly hollows: HollowDef[];
  readonly tunnels: TunnelDef[];
  readonly field = new GravityField();
  readonly group = new THREE.Group();
  private readonly gpu: HollowGpu[];
  private readonly colliders: HollowWallCollider[] = [];
  private readonly sunLights: { light: THREE.PointLight; hollow: HollowDef }[] = [];
  readonly lantern: THREE.PointLight;

  constructor(
    private readonly origin: RenderOrigin,
    scene: THREE.Scene,
  ) {
    const a = makeHollow({
      id: 'amber',
      name: 'Amberhollow',
      center: [1.0e7, 2.0e5, -3.0e6],
      radius: 2000,
      seed: 11,
      sun: { radius: 28, color: [1.0, 0.93, 0.8], intensity: 3.2 },
    });
    const axis = new THREE.Vector3(0.36, 0.8, -0.48).normalize();
    const bCenter = a.center.clone().addScaledVector(axis, 2000 + 300 + 1000);
    const b = makeHollow({
      id: 'teal',
      name: 'Tealhollow',
      center: [bCenter.x, bCenter.y, bCenter.z],
      radius: 1000,
      seed: 23,
      sun: { radius: 15, color: [0.8, 0.95, 1.0], intensity: 2.6 },
    });
    this.hollows = [a, b];
    for (const h of this.hollows) this.field.add({ center: h.center, radius: h.radius, strength: h.gravity });

    this.tunnels = [
      buildDropShaft(a, b, 6),
      buildSpiralTunnel(a, b, { tubeRadius: 4, helixRadius: 22, pitchDeg: 24, depth: 8, phase: 0.6 }),
    ];

    this.gpu = this.hollows.map(makeHollowGpu);
    this.lantern = new THREE.PointLight(0xffd29a, 60, 45, 2);
    scene.add(this.lantern);
    scene.add(this.group);

    for (const h of this.gpu) this.buildHollow(h, scene);
    const leak = new THREE.Color(1.0, 0.95, 0.85).multiplyScalar(1.4);
    const tunnelMat = createTunnelMaterial(this.gpu, lights([this.lantern]), leak);
    for (const t of this.tunnels) {
      const anchor = t.path[Math.floor(t.path.length / 2)].clone();
      const data = buildTube(t.path, t.radius, 28, anchor);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
      geo.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
      geo.setAttribute('uv', new THREE.BufferAttribute(data.uvs, 2));
      geo.setIndex(new THREE.BufferAttribute(data.indices, 1));
      geo.computeBoundingSphere();
      const mesh = new THREE.Mesh(geo, tunnelMat);
      mesh.frustumCulled = false;
      mesh.name = t.name;
      this.group.add(mesh);
      origin.anchor(mesh, anchor);
    }
  }

  private buildHollow(h: HollowGpu, scene: THREE.Scene): void {
    const def = h.def;
    const sunDef = def.sun!;
    const sunColor = new THREE.Color(...sunDef.color);
    // A point light at the centre lights every part of the inner wall correctly,
    // including the far side overhead. Intensity gives the requested irradiance at the wall.
    const sun = new THREE.PointLight(sunColor, sunDef.intensity * def.radius * def.radius, 0, 2);
    scene.add(sun);
    this.sunLights.push({ light: sun, hollow: def });
    const ambient = new THREE.AmbientLight(new THREE.Color(0.55, 0.62, 0.7), 0.45);
    scene.add(ambient);

    const mouths = this.tunnels.flatMap((t) => t.mouths.filter((m) => m.hollow === def));
    const wallSegs = def.radius > 1500 ? 640 : 420;
    const geo = new THREE.SphereGeometry(def.radius, wallSegs, wallSegs / 2);
    flipInside(geo);
    const wall = new THREE.Mesh(geo, createWallMaterial(h, mouths, lights([sun, ambient, this.lantern])));
    wall.frustumCulled = false;
    wall.name = `${def.name} wall`;
    this.group.add(wall);
    this.origin.anchor(wall, def.center);

    const sunMesh = new THREE.Mesh(
      new THREE.IcosahedronGeometry(sunDef.radius, 5),
      createSunMaterial(sunColor, 40),
    );
    sunMesh.name = `${def.name} sun`;
    this.group.add(sunMesh);
    this.origin.anchor(sunMesh, def.center);
    this.origin.anchor(sun, def.center);
  }

  /** Create collision once physics exists. */
  initPhysics(physics: PhysicsWorld): void {
    for (const h of this.hollows) {
      const mouths = this.tunnels.flatMap((t) => t.mouths.filter((m) => m.hollow === h));
      this.colliders.push(new HollowWallCollider(physics, h, mouths));
    }
    for (const t of this.tunnels) {
      const anchor = t.path[Math.floor(t.path.length / 2)].clone();
      const data = buildTube(t.path, t.radius, 20, anchor);
      const idx: number[] = [];
      const p = new THREE.Vector3();
      const inside = (i: number) => {
        p.set(data.positions[i * 3], data.positions[i * 3 + 1], data.positions[i * 3 + 2]).add(anchor);
        return this.hollows.some((h) => p.distanceTo(h.center) < h.radius - 0.05);
      };
      for (let i = 0; i < data.indices.length; i += 3) {
        const a = data.indices[i];
        const b = data.indices[i + 1];
        const c = data.indices[i + 2];
        if (inside(a) && inside(b) && inside(c)) continue;
        idx.push(a, b, c);
      }
      physics.addStaticMesh(anchor, data.positions, new Uint32Array(idx));
    }
  }

  /** Spawn on Amberhollow's wall, ~60 m from the tunnel mouths, facing them. */
  spawn(player: PlayerController): void {
    const a = this.hollows[0];
    const mouth = this.tunnels[0].mouths[0].point;
    const toMouth = mouth.clone().sub(a.center).normalize();
    const side = new THREE.Vector3().crossVectors(toMouth, new THREE.Vector3(0, 1, 0)).normalize();
    const dir = toMouth.clone().multiplyScalar(a.radius).addScaledVector(side, 70).normalize();
    const pos = a.center.clone().addScaledVector(dir, a.radius - PLAYER_CENTER_HEIGHT - 0.05);
    player.teleport(pos, dir.clone().negate());
    player.faceTowards(mouth);
  }

  update(player: PlayerController): void {
    for (const c of this.colliders) c.update(player.position);
  }

  /** Per-frame camera-relative updates that aren't plain anchored objects. */
  frame(): void {
    // Camera and render origin are already updated by the game loop.
    for (const h of this.gpu) this.origin.toRender(h.def.center, h.centerRel.value);
    this.lantern.position.set(0.25, -0.25, 0);
  }

  /** The hero's bounce light: the hollows' ambient (it lights everything, both are in the scene). */
  heroLight(_p: THREE.Vector3, out: THREE.Color): THREE.Color {
    return out.setRGB(0.55, 0.62, 0.7).multiplyScalar(0.45 * this.hollows.length);
  }

  /** The sun of the hollow the hero stands in, from its centre (none in a tunnel). */
  heroSun(p: THREE.Vector3, dir: THREE.Vector3, out: THREE.Color): THREE.Color {
    const s = this.sunLights.find((l) => l.hollow === this.hollowAt(p));
    if (!s) return out.setRGB(0, 0, 0);
    dir.subVectors(s.hollow.center, p);
    const d2 = Math.max(dir.lengthSq(), 1);
    dir.normalize();
    return out.copy(s.light.color).multiplyScalar(s.light.intensity / d2);
  }

  /** Which hollow contains p, if any. */
  hollowAt(p: THREE.Vector3): HollowDef | null {
    for (const h of this.hollows) if (p.distanceTo(h.center) < h.radius) return h;
    return null;
  }
}

/** Turn an outward sphere into an inward-facing one (normals and winding). */
export function flipInside(geo: THREE.BufferGeometry): void {
  const n = geo.getAttribute('normal') as THREE.BufferAttribute;
  for (let i = 0; i < n.count; i++) n.setXYZ(i, -n.getX(i), -n.getY(i), -n.getZ(i));
  const index = geo.getIndex()!;
  for (let i = 0; i < index.count; i += 3) {
    const b = index.getX(i + 1);
    index.setX(i + 1, index.getX(i + 2));
    index.setX(i + 2, b);
  }
}
